import { Immutable } from "immer";
import React, { type JSX } from "react";
import { FilterDistanceSelect } from "tsi-common-react/src/apps/retail/FilterDistanceSelect/index";
import { MarkerMap } from "tsi-common-react/src/apps/retail/MarkerMap/index";
import { formatDistanceWithSuffix } from "tsi-common-react/src/apps/retail/helpers";
import { Link } from "tsi-common-react/src/components/Link/index";
import { LocationInput } from "tsi-common-react/src/components/LocationInput/index";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import {
    SyncStoreID,
    isoPhoneE164,
    isoPostcode,
    isoStateA2,
} from "tsi-common-react/src/models/nominals";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import { roundCoordinatePrecision } from "tsi-common-react/src/utils/maps";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useSurveyTrigger } from "../StoreLocatorSurvey/context";

import styles from "./index.module.scss";

interface ILocationProps {
    store: Immutable<RetailStore>;
    isFocused: boolean;
}

interface IContainerProps {
    stores: Immutable<RetailStore[]>;
    focusedStoreID: SyncStoreID | null;
    location: ILocation | null;
    onBeforeInfoWindowOpen: (
        info: google.maps.InfoWindow,
        store: Immutable<RetailStore>,
    ) => void;
    onInfoWindowClose: () => void;
}

const RetailLocation = (props: ILocationProps) => {
    const staticURL = useStaticURL();
    // encode destination address for Directions link
    const destinationAddress = encodeURI(
        props.store.address +
            " " +
            props.store.address2 +
            " " +
            props.store.city +
            "," +
            isoStateA2.unwrap(props.store.state) +
            " " +
            isoPostcode.unwrap(props.store.postal),
    );
    const storeDataStyle = {
        display: "none",
    };
    let websiteLinkElement: JSX.Element | null = null;
    if (props.store.web_stearns) {
        // TODO(i18n): Use t tagged template: {t`Website`}
        websiteLinkElement = (
            <Link
                className={styles.locWebsite}
                href={props.store.web_stearns}
                target="_blank"
                rel="nofollow noreferrer"
            >
                Website
                {/* TODO(i18n): Use t tagged template for alt text: alt={t`Website`} */}
                <img
                    src={`${staticURL}img/retail-locator/open-link-icon-blue.png`}
                    alt="Website"
                />
            </Link>
        );
    }
    return (
        <article
            className={props.isFocused ? styles.isCurrent : undefined}
            role="group"
            aria-label="location"
        >
            <h3>{props.store.name}</h3>
            <p>{props.store.address}</p>
            {props.store.address2 && <p>{props.store.address2}</p>}
            <p>
                {props.store.city}, {isoStateA2.unwrap(props.store.state)}{" "}
                {isoPostcode.unwrap(props.store.postal)}
            </p>
            {props.store.phone && (
                <a href={`tel:${props.store.phone}`}>
                    {formatPhoneNumber(props.store.phone)}
                </a>
            )}
            <p>
                {props.store.distance &&
                    formatDistanceWithSuffix(props.store.distance, 2)}
            </p>
            {/* TODO(i18n): Use t tagged template: {t`Directions`} */}
            <a
                className={styles.locDirections}
                href={`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                target="_blank"
                rel="noreferrer"
            >
                Directions
            </a>
            {websiteLinkElement}
            <div className="store-data ada-hidden" style={storeDataStyle}>
                <span className="loc-addr">{props.store.address}</span>
                <span className="loc-addr2">{props.store.address2}</span>
                <span className="loc-city">{props.store.city}</span>
                <span className="loc-distance">
                    {props.store.distance &&
                        formatDistanceWithSuffix(props.store.distance, 2)}
                </span>
                <span className="loc-name">{props.store.name}</span>
                <span className="loc-phone">
                    {props.store.phone
                        ? isoPhoneE164.unwrap(props.store.phone)
                        : ""}
                </span>
                <span className="loc-state">
                    {isoStateA2.unwrap(props.store.state)}
                </span>
                <span className="loc-postal">
                    {isoPostcode.unwrap(props.store.postal)}
                </span>
                {props.store.lat && props.store.lng && (
                    <>
                        <span className="loc-lat">
                            {String(
                                roundCoordinatePrecision(props.store.lat) || "",
                            )}
                        </span>
                        <span className="loc-lng">
                            {String(
                                roundCoordinatePrecision(props.store.lng) || "",
                            )}
                        </span>
                    </>
                )}
                <span className="loc-directions-url">
                    {`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                </span>
            </div>
        </article>
    );
};

const pushLocationSearchEvent = (searchText: string): void => {
    const window = getWindow();
    if (window?.dataLayer?.push) {
        window.dataLayer.push({
            event: "Zip Entry Click",
            ZipCode: searchText,
        });
    }
};

// Google Maps draws markers on a canvas, so CSS can't color them. Read the
// --retail-locator--map-pin-color token (blocks/elements/_colors.scss) when
// the marker is built instead, so the palette stays the single source of the
// color: Sapphire in v2026, the live winterberry in v2023. Markers are only
// built after the map loads in the browser, where the global token is always
// set; there's deliberately no hex fallback, since one value can't be right
// for both versions (Maps uses its default stroke if this is ever empty).
const getMapPinColor = (): string | null => {
    const root = getDocument()?.documentElement;
    const win = getWindow();
    if (!root || !win) {
        return null;
    }
    const color = win
        .getComputedStyle(root)
        .getPropertyValue("--retail-locator--map-pin-color")
        .trim();
    return color || null;
};

const getMarkerOptions = (): google.maps.MarkerOptions => {
    return {
        icon: {
            strokeColor: getMapPinColor(),
            strokeWeight: 2,
            scale: 1.5,
            path: `M7.61905 20C7.61905 20 15.2381 11.8269 15.2381 7.61905C15.2381 3.41117 11.8269 0 7.61905 0C3.41117 0 0 3.41117 0 7.61905C0
            11.8269 7.61905 20 7.61905 20ZM7.61905 11.4286C9.72302 11.4286 11.4286 9.72299 11.4286 7.61905C11.4286 5.51511 9.72302 3.80952
            7.61905 3.80952C5.51508 3.80952 3.80952 5.51511 3.80952 7.61905C3.80952 9.72299 5.51508 11.4286 7.61905 11.4286Z`,
        },
    };
};

export const RetailLocatorBlock = (props: IContainerProps): React.ReactNode => {
    const staticURL = useStaticURL();
    const triggerSurvey = useSurveyTrigger();
    const locationInputID = "retail-locator-location-input";
    const distanceInputID = "retail-locator-distance-input";

    const onAfterLocationChange = (searchText: string): void => {
        pushLocationSearchEvent(searchText);
        triggerSurvey(props.location?.formatted_address);
    };

    return (
        <>
            <form
                className={styles.form}
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                {/* TODO(i18n): Use t tagged template: <label htmlFor={locationInputID}>{t`Find local Stearns & Foster retailers by entering your location here.`}</label> */}
                <label htmlFor={locationInputID}>
                    Find local Stearns &amp; Foster retailers by entering your
                    location here.
                </label>
                <LocationInput
                    inputFormat="short"
                    inputProps={{
                        id: locationInputID,
                        // TODO(i18n): Use t tagged template: placeholder: t`Enter your zip code`
                        placeholder: "Enter your zip code",
                    }}
                    onAfterChange={onAfterLocationChange}
                />
                <button className="al-location--submit-zip" type="submit">
                    {/* TODO(i18n): Use t tagged template for alt text: alt={t`Search Icon`} */}
                    <img
                        src={`${staticURL}img/icons/search.svg`}
                        alt="Search Icon"
                    />
                </button>
                <label
                    className={styles.distanceWrapper}
                    htmlFor={distanceInputID}
                >
                    <FilterDistanceSelect
                        selectProps={{
                            id: distanceInputID,
                            name: "bh-sl-maxdistance",
                            title: t`Retailer Distance`,
                        }}
                    />
                </label>
            </form>
            <div className={styles.results}>
                <MarkerMap
                    stores={props.stores}
                    defaultCenter={props.location}
                    markerOptions={getMarkerOptions}
                    elemProps={{
                        className: styles.map,
                    }}
                    onBeforeInfoWindowOpen={props.onBeforeInfoWindowOpen}
                    onInfoWindowClose={props.onInfoWindowClose}
                />
                <div
                    className={concatClassNames([
                        styles.locations,
                        // JS hook: queried by onInfoWindowClose
                        "locations",
                    ])}
                >
                    {props.location && (
                        // TODO(i18n): Use ngettext for plurals and interpolate address: ngettext(msgid`${props.stores.length} location near ${props.location.formatted_address}`, `${props.stores.length} locations near ${props.location.formatted_address}`, props.stores.length)
                        <h5 className="marked">
                            {props.stores.length} locations near{" "}
                            {props.location.formatted_address}
                        </h5>
                    )}
                    {props.stores.map((store) => (
                        <RetailLocation
                            {...props}
                            key={`${store.external_id}`}
                            store={store}
                            isFocused={
                                props.focusedStoreID === store.external_id
                            }
                        />
                    ))}
                </div>
            </div>
        </>
    );
};
